<template>
  <div>
    <q-card style="padding: 15px" v-if="selectedItems.length === 0">
      <empty-value name="vehicle"></empty-value>
    </q-card>

    <div class="content-table" ref="table" v-else>
      <q-btn
        v-if="selectedItems.length > 0"
        class="glossy custom-item-icon-3 left-0"
        style="margin-left: -10px"
        @mousedown="shiftLeft()"
        @mouseup="shiftLeftEnd()"
        round
        color="primary"
        icon="fas fa-arrow-left"
      />
      <q-btn
        v-if="selectedItems.length > 0"
        class="glossy custom-item-icon-3 right-0"
        @mouseup="shiftRightEnd()"
        @mousedown="shiftRight()"
        round
        color="primary"
        icon="fas fa-arrow-right"
      />
      <q-form ref="postSellVehiclesFormRef"
              @submit.prevent.stop="postSellVehicles"
              @validation-error="onValidationError"
              :disabled="isLoading">
        <table>
          <thead>
          <tr>
            <th v-for="column in columns">{{ column.name }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in selectedItems" :key="item.id">
            <td v-for="column in columns" v-if="column.visible">
              <div v-if="column.val === 'delete'">
                <q-btn round color="red" icon="fas fa-trash" size="sm" @click="deleteItem(item.id)"/>
              </div>
              <div v-else-if="column.val === 'company'">{{ item[column.val].name }}</div>
              <div v-else-if="column.val === 'location'">{{ item[column.val].code }}</div>
              <div v-else-if="column.val === 'group'">{{ item.group_name }}</div>
              <div v-else-if="column.val === 'class'">{{ item.class_name }}</div>
              <div v-else-if="column.val === 'make'">{{ item.brand.name }}</div>
              <div v-else-if="column.val === 'model'">{{ item.model.name }}</div>
              <div v-else-if="column.val === 'model_detail'">{{ item.model_detail.name }}</div>
              <div v-else-if="column.val === 'auction'">{{ getUsdValue(item.auction) ?? '-' }}</div>
              <div v-else-if="column.val === 'due_location_name'" class="fix-col-lg input-col">
                <q-input outlined dense v-model="item.location.due_location_name"
                       :rules="[val => !!val || 'Field is required']"
                       :disable="isLoading"/>
              </div>
              <div v-else-if="column.val === 'pickup_location_number'" class="fix-col-lg input-col">
                <q-input outlined dense v-model="item.location.pickup_location_number"
                       :rules="[val => !!val || 'Field is required']"
                       :disable="isLoading"/>
              </div>
              <div v-else-if="column.val === 'mileage'" class="fix-col input-col">
                <q-field outlined dense v-model.lazy="item.last_mileage_record.mileage"
                       :rules="[val => !!val || 'Field is required']"
                       :disable="isLoading">
                  <template v-slot:control="{ id, floatingLabel, value, emitValue }">
                    <input :id="id" class="q-field__input" :value="value"
                           @change="e => emitValue(e.target.value)" v-money="mileageFormatForDirective"
                           v-show="floatingLabel">
                  </template>
                </q-field>
              </div>
              <div v-else-if="column.val === 'price'" class="fix-col-lg input-col">
                <q-field outlined dense v-model.lazy="item.sell_price"
                       :rules="[val => !!val || 'Field is required']"
                       :disable="isLoading">
                  <template v-slot:control="{ id, floatingLabel, value, emitValue }">
                    <input :id="id" class="q-field__input" :value="value"
                           @change="e => emitValue(e.target.value)" v-money="moneyFormatForDirective"
                           v-show="floatingLabel">
                  </template>
                </q-field>
              </div>
              <div v-else-if="column.val === 'sell_auction_price'" class="fix-col">{{ getSellAuctionPrice(item) }}</div>
              <div v-else-if="column.val === 'grade'" class="fix-col">
                <q-input outlined dense v-model="item.grade" type="number" min="1" max="5" step="any"
                         :disable="isLoading"/>
              </div>
              <div v-else-if="column.val === 'initial_cost'" class="fix-col-xl input-col">
                <div style="display: flex; flex-direction: row;">
                  <q-checkbox v-model.lazy="item.initial_cost_selection" style="margin-top: -20px;"></q-checkbox>
                  <q-field outlined dense v-model.lazy="item.initial_cost"
                           :rules="[val => !!val || 'Field is required']"
                           :disable="isLoading || !item.initial_cost_selection">
                    <template v-slot:control="{ id, floatingLabel, value, emitValue }">
                      <input :id="id" class="q-field__input" :value="value"
                             :disabled="isLoading || !item.initial_cost_selection"
                             @change="e => emitValue(e.target.value)" v-money="moneyFormatForDirective"
                             v-show="floatingLabel" >
                    </template>
                  </q-field>
                </div>
              </div>
              <div v-else-if="column.val === 'retail'" class="fix-col">
                <q-toggle v-model.lazy="item.retail" color="teal"/>
              </div>
              <div v-else-if="column.val === 'wholesale'" class="fix-col">
                <q-toggle v-model.lazy="item.wholesale" color="teal"/>
              </div>
              <div v-else-if="column.val === 'status'" class="fix-col input-col">
                <q-input outlined dense v-model="item.sell_status"
                                 :rules="[val => !!val || 'Field is required']"
                                 :disable="isLoading"/>
              </div>
              <div v-else-if="column.val === 'is_with_mmr'">
                <q-toggle v-model.lazy="item.is_with_mmr" color="teal"/>
              </div>
              <div v-else>{{ item[column.val] }}</div>
            </td>
          </tr>
          </tbody>
        </table>
      </q-form>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import {VMoney} from "v-money";
import EmptyValue from "@/components/EmptyValue.vue";

export default {
  name: 'SelectionList',
  components: {EmptyValue},
  props: {
    onDeleteItem: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      columns: [],
      results: [],
      isLoading: false,
      mileageFormatForDirective: {
        decimal: '.',
        thousands: ',',
        prefix: '',
        suffix: '',
        precision: 0,
        masked: false
      },
      moneyFormatForDirective: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false
      }
    }
  },
  computed: {
    selectionListColumns: {
      get() {
        return this.$store.state.forSale.selectionListColumns;
      },
      set(val) {
        this.$store.commit("forSale/SET_SELECTION_LIST_COLUMNS", val);
      },
    },
    selectedItems: {
      get() {
        return this.$store.state.forSale.selectedItems;
      },
      set(val) {
        this.$store.commit("forSale/SET_SELECTED_ITEMS", val);
      },
    },
    isSellVehicles: {
      get() {
        return this.$store.state.forSale.isSellVehicles
      },
      set(val) {
        this.$store.commit('forSale/SET_IS_SELL_VEHICLES', val)
      }
    },
    selectedProvider: {
      get() {
        return this.$store.state.forSale.selectedProvider
      },
      set(val) {
        this.$store.commit('forSale/SET_SELECTED_PROVIDER', val)
      }
    },
  },
  methods: {
    shiftRight() {
      this.rightInterval = setInterval(() => {
        this.$refs.table.scrollLeft += 50
      }, 50)
    },
    shiftRightEnd() {
      clearInterval(this.rightInterval)
    },
    shiftLeft() {
      this.leftInterval = setInterval(() => {
        this.$refs.table.scrollLeft -= 50
      }, 50)
    },
    shiftLeftEnd() {
      clearInterval(this.leftInterval)
    },
    getSellAuctionPrice(item) {
      return this.getUsdValue(parseInt(item.sell_price.toString().replace(/\D/g,'')) / 100 - item.auction)
    },
    deleteItem(id) {
      this.onDeleteItem(id)
    },
    getUsdValue(value) {
      return value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      })
    },
    onValidationError(ref) {
      this.$q.notify({
        message: 'Please fill out all required fields',
        timeout: 250,
        position: 'top-right',
        progress: true,
        type: 'negative'
      })
      ref.$el.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      })
    },
    async postSellVehicles() {
      try {
        this.isLoading = true
        const postData = []
        this.selectedItems.forEach(item => {
          const itemData = {
            vehicle_id: item.id,
            due_location_name: item.location.due_location_name,
            pickup_location_number: item.location.pickup_location_number,
            grade: item.grade !== '' ? parseFloat(item.grade) : null,
            mileage: parseInt(item.last_mileage_record.mileage.replace(',', '')),
            status: item.sell_status,
            price: parseInt(item.sell_price
              .replace('$ ', '')
              .replace('.', '')
              .replace(',', '')) / 100,
            initial_cost: item.initial_cost_selection && (item.initial_cost !== null || item.initial_cost !== '') ?
              parseInt(item.initial_cost
              .replace('$ ', '')
              .replace('.', '')
              .replace(',', '')) / 100 : null,
            is_with_mmr: item.is_with_mmr,
            retail: item.retail ?? false,
            wholesale: item.wholesale ?? true,
          }

          if (postData.some(data => data.company_id === item.company.id)) {
            const companyData = postData.find(data => data.company_id === item.company.id)
            companyData.items.push(itemData)
            // Ensure provider is set on existing company objects
            if (!companyData.provider) {
              companyData.provider = this.selectedProvider
            }
          } else {
            postData.push({
              company_id: item.company.id,
              provider: this.selectedProvider,
              items: [itemData]
            })
          }
        })

        const {data} = await axios.put('/sell', postData)
        this.$q.notify({
          message: 'Vehicles sold successfully',
          timeout: 250,
          position: 'top-right',
          progress: true,
          type: 'positive'
        })
        this.results = data.data
        this.selectedItems = []
      } catch (error) {
        this.$q.notify({
          message: error.message,
          timeout: 250,
          position: 'top-right',
          progress: true,
          type: 'negative'
        })
      } finally {
        this.isLoading = false
        this.isSellVehicles = false
      }
    },
  },
  watch: {
    selectionListColumns: {
      deep: true,
      immediate: true,
      handler(columns) {
        this.columns = columns.filter(col => col.visible)
      }
    },
    isSellVehicles: {
      deep: true,
      immediate: true,
      async handler(val) {
        if (val) {
          const valid = await this.$refs.postSellVehiclesFormRef.validate()
          if (valid) {
            await this.postSellVehicles()
          } else {
            this.isSellVehicles = false
          }
        }
      }
    },
  },
  directives: {money: VMoney},
}
</script>

<style scoped lang="scss">
.custom-item-icon-3 {
  z-index: 999 !important;
  position: fixed !important;
  top: 60% !important;
}
.right-0 {
  right: 0;
}
.table-cell {
  display: table-cell;
}
.none-cell {
  display: none;
}
.selected-finance {
  background: rgba(244, 67, 54, 0.5) !important;
}
.no-finance {
  background: rgba(244, 67, 54, 0.1) !important;
}
.tag-table-container {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  .tag-item {
    margin-right: -5px;
    height: 30px;
    width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 50%;
  }
}
.mark-table {
  background: rgba(0, 0, 0, 0.12) !important;
}
.flip-list-move {
  transition: transform 500ms;
}
.content-table {
  position: relative;
  z-index: 1;
  overflow: auto;
}
.content-table table {
  min-width: auto !important;
  border-collapse: separate;
  border-spacing: 0;
}
.table-wrap {
  position: relative;
}
.content-table {
  th {
    align-items: center;
    padding: 15px 5px;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    vertical-align: center;
    text-align: center;
  }
  td {
    padding: 15px 10px;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    vertical-align: center;
    text-align: center;
    cursor: auto;
  }
}
@media only screen and (max-width: 600px) {
  .content-table th,
  .content-table td {
    padding: 15px 5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    vertical-align: center;
    text-align: left;
  }
}
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.container-checkbox {
  width: 18px;
  cursor: pointer;
  height: 18px;
  text-align: center;
  border: 2px solid rgba(0, 0, 0, 0.54);
}
.input-col {
  padding: 30px 10px 10px !important;
}
.fix-col {
  min-width: 100px;
}
.fix-col-lg {
  min-width: 130px;
}
.fix-col-xl {
  min-width: 170px;
}
</style>
